/**
 * ============================================================================
 * CUSTOM ADD TO CART - Rate Limit Protection
 * ============================================================================
 * 
 * This file provides rate limiting and request management for cart operations.
 * 
 * FEATURES:
 * - Strict rate limiting (500ms minimum between requests)
 * - Duplicate request blocking
 * - Optional subscription validation (currently disabled)
 * 
 * CONFIGURATION:
 * - ENABLE_SUBSCRIPTION_VALIDATION: Set to true to enable subscription features
 * - MIN_DELAY_BETWEEN_REQUESTS: Adjust rate limit delay (default: 500ms)
 * 
 * PERFORMANCE:
 * - Current mode (subscription disabled): Fast, minimal API calls
 * - Subscription mode: Slower, validates cart before add (extra /cart.js call)
 * 
 * TO ENABLE SUBSCRIPTION FEATURES:
 * 1. Set ENABLE_SUBSCRIPTION_VALIDATION = true
 * 2. Ensure mixTypeWarningModal exists in your theme
 * 3. Test thoroughly before deploying
 * ============================================================================
 */

// ============================================================================
// CONFIGURATION
// ============================================================================
const ENABLE_SUBSCRIPTION_VALIDATION = false; // Set to true to enable subscription features

// ============================================================================
// Cart Request Manager - Strict rate limiting with request blocking
// ============================================================================
const CartRequestManager = (() => {
  let activeRequests = new Map(); // key -> { inProgress: boolean, lastCompleted: timestamp }
  const MIN_DELAY_BETWEEN_REQUESTS = 500; // ms - minimum time between requests
  
  return {
    // Execute request with strict rate limiting
    async execute(key, fn) {
      const state = activeRequests.get(key) || { inProgress: false, lastCompleted: 0 };
      
      // Block if request is already in progress
      if (state.inProgress) {
        console.log(`🛑 Request "${key}" already in progress, blocking...`);
        const error = new Error('Request already in progress');
        error.isDuplicateRequest = true;
        throw error;
      }
      
      // Check if enough time has passed since last request
      const now = Date.now();
      const timeSinceLastRequest = now - state.lastCompleted;
      
      if (timeSinceLastRequest < MIN_DELAY_BETWEEN_REQUESTS && state.lastCompleted > 0) {
        const waitTime = MIN_DELAY_BETWEEN_REQUESTS - timeSinceLastRequest;
        console.log(`⏳ Rate limit: waiting ${waitTime}ms before next request...`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
      
      // Mark as in progress
      state.inProgress = true;
      activeRequests.set(key, state);
      
      try {
        const result = await fn();
        state.lastCompleted = Date.now();
        return result;
      } finally {
        state.inProgress = false;
        activeRequests.set(key, state);
      }
    },
    
    // Check if request is in progress
    isInProgress: (key) => {
      const state = activeRequests.get(key);
      return state ? state.inProgress : false;
    },
    
    // Clear all state
    clearAll: () => {
      activeRequests.clear();
    }
  };
})();

// ============================================================================
// SUBSCRIPTION VALIDATION MODULE (Optional - Currently Disabled)
// ============================================================================
// To enable: Set ENABLE_SUBSCRIPTION_VALIDATION = true
// This module validates subscription/one-time product mixing before add to cart
const SubscriptionValidator = (() => {
  if (!ENABLE_SUBSCRIPTION_VALIDATION) {
    return null; // Module disabled
  }

  const MIX_TYPE_MODAL_ID = "mixTypeWarningModal";
  
  const hasSubscription = (cart) =>
    cart.items?.some((item) => !!item.selling_plan_allocation);

  const getSubscriptionPlanIds = (cart) =>
    cart.items
      .filter((item) => item.selling_plan_allocation)
      .map((item) => item.selling_plan_allocation.selling_plan.id);

  const toggleMixTypeWarning = (show) => {
    const mixTypeWarning = document.getElementById(MIX_TYPE_MODAL_ID);
    if (mixTypeWarning) {
      mixTypeWarning.style.display = show ? "flex" : "none";
    }
  };

  const savedCurrentSelectedProducts = (products) => {
    if (!products || products.length === 0) return;

    const existing = localStorage.getItem("savedSelectedProducts");
    let storedProducts = [];

    if (existing) {
      try {
        storedProducts = JSON.parse(existing);
      } catch (e) {
        console.warn("Failed to parse savedSelectedProducts:", e);
        storedProducts = [];
      }
    }

    const normalizedStored = storedProducts.map((p) => ({
      ...p,
      id: String(p.id),
      selling_plan: p.selling_plan ? String(p.selling_plan) : null,
    }));

    const normalizedNew = products.map((p) => ({
      ...p,
      id: String(p.id),
      selling_plan: p.selling_plan ? String(p.selling_plan) : null,
    }));

    const merged = [
      ...normalizedStored,
      ...normalizedNew.filter(
        (p) =>
          !normalizedStored.some(
            (sp) => sp.id === p.id && sp.selling_plan === p.selling_plan
          )
      ),
    ];

    localStorage.setItem("savedSelectedProducts", JSON.stringify(merged));
  };

  return {
    // Validate if adding product is allowed based on cart contents
    async validateAddToCart(productData, originalFetch) {
      try {
        const oldCart = await originalFetch("/cart.js").then((r) => r.json());
        
        const addingIsSubscription = (() => {
          if (Array.isArray(productData.items)) {
            return productData.items.some((item) => !!item.selling_plan);
          }
          return !!productData.selling_plan;
        })();

        const cartHasSubscription = hasSubscription(oldCart);
        const selectedProducts = Array.isArray(productData.items)
          ? productData.items
          : [productData];

        // Case 1: Cart has subscription, trying to add non-subscription
        if (cartHasSubscription && !addingIsSubscription) {
          console.warn("Cannot add non-subscription item when cart has subscription items");
          savedCurrentSelectedProducts(selectedProducts);
          toggleMixTypeWarning(true);
          const error = new Error("Mixing subscription and one-time items not allowed");
          error.isMixTypeError = true;
          throw error;
        }

        // Case 2: Cart has non-subscription, trying to add subscription
        if (!cartHasSubscription && addingIsSubscription && oldCart.items.length > 0) {
          console.warn("Cannot add subscription item when cart has non-subscription items");
          savedCurrentSelectedProducts(selectedProducts);
          toggleMixTypeWarning(true);
          const error = new Error("Mixing subscription and one-time items not allowed");
          error.isMixTypeError = true;
          throw error;
        }

        return { valid: true, oldCart };
      } catch (err) {
        if (err.isMixTypeError) {
          throw err;
        }
        console.warn("Failed to validate cart:", err);
        return { valid: true, oldCart: null };
      }
    }
  };
})();

// ============================================================================
// SUBSCRIPTION MODAL HANDLERS (Optional - Currently Disabled)
// ============================================================================
if (ENABLE_SUBSCRIPTION_VALIDATION) {
  document.addEventListener("DOMContentLoaded", () => {
    const MIX_TYPE_MODAL_ID = "mixTypeWarningModal";
    const mixTypeWarning = document.getElementById(MIX_TYPE_MODAL_ID);
    const cartButton = document.getElementById("cart-icon-bubble");
    const cancelButton = document.querySelector(".modal-button.cancel-button");

    const addProductToCart = async (variantId, quantity, sellingPlanId = null) => {
      const formData = new FormData();
      formData.append("id", variantId);
      formData.append("quantity", quantity);
      if (sellingPlanId) formData.append("selling_plan", sellingPlanId);
      const response = await fetch("/cart/add.js", {
        method: "POST",
        body: formData,
      });
      if (!response.ok) throw new Error("Failed to add product to cart");
      return await response.json();
    };

    const handleModalEvent = async (isConfirmed, id, source) => {
      if (source === "overlay") {
        window.OpusNoATC = true;
        localStorage.removeItem("savedSelectedProducts");
        if (mixTypeWarning) {
          mixTypeWarning.style.display = "none";
        }
        return;
      }

      if (id === MIX_TYPE_MODAL_ID) {
        await mixTypeHandle(isConfirmed);
      }
    };

    const mixTypeHandle = async (isConfirmed) => {
      if (isConfirmed) {
        localStorage.removeItem("savedSelectedProducts");
        window.location.href = "/cart";
        return;
      }
      
      const normalCancelText = cancelButton.innerText;
      cancelButton.innerText = window.subscriptionModalText?.loading || "Loading...";
      
      try {
        const response = await fetch("/cart/clear", { method: "POST" });
        if (!response.ok) {
          console.error("Failed to clear cart");
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, 100));

        let cartEmpty = false;
        let retries = 0;
        while (!cartEmpty && retries < 5) {
          const cartCheck = await fetch("/cart.js").then((r) => r.json());
          if (cartCheck.items.length === 0) {
            cartEmpty = true;
          } else {
            await new Promise((resolve) => setTimeout(resolve, 100));
            retries++;
          }
        }

        if (!cartEmpty) {
          console.error("Cart could not be cleared");
          return;
        }

        const selectedProducts = localStorage.getItem("savedSelectedProducts");
        if (selectedProducts) {
          const products = JSON.parse(selectedProducts);
          for (const product of products) {
            await addProductToCart(
              product.id,
              product.quantity || 1,
              product.selling_plan
            );
          }
          localStorage.removeItem("savedSelectedProducts");
        }
        
        if (cartButton) cartButton.click();
      } catch (error) {
        console.error("Error handle mixtype:", error);
      } finally {
        mixTypeWarning.style.display = "none";
        window.OpusNoATC = false;
        cancelButton.innerText = normalCancelText;
      }
    };

    window.addEventListener("info-modal:confirm", (e) => {
      handleModalEvent(true, e.detail.id, e.detail.source);
    });

    window.addEventListener("info-modal:cancel", (e) => {
      handleModalEvent(false, e.detail.id, e.detail.source);
    });
  });
}

// ============================================================================
// MAIN ADD TO CART LOGIC
// ============================================================================
(() => {
  document.addEventListener("DOMContentLoaded", () => {
    const originalFetch = window.fetch;
    const originalXHROpen = XMLHttpRequest.prototype.open;

    const fire = (name, detail = {}) =>
      window.dispatchEvent(new CustomEvent(name, { detail }));

    window.fetch = async function (...args) {
      const [url, options] = args;
      const urlString = typeof url === 'string' ? url : url.toString();

      // Handle /cart/add with rate limiting
      if (urlString.includes("/cart/add")) {
        const requestKey = 'cart-add';
        
        // Block if request is already in progress
        if (CartRequestManager.isInProgress(requestKey)) {
          console.log('🛑 Add to cart blocked: request in progress');
          const error = new Error('Add to cart request already in progress');
          error.isDuplicateRequest = true;
          return Promise.reject(error);
        }

        // Optional: Validate subscription mixing (if enabled)
        if (ENABLE_SUBSCRIPTION_VALIDATION && SubscriptionValidator) {
          try {
            let productData = {};
            if (options?.body instanceof FormData) {
              productData = Object.fromEntries(options.body.entries());
            } else if (typeof options?.body === "string") {
              productData = JSON.parse(options.body);
            }
            
            const validation = await SubscriptionValidator.validateAddToCart(productData, originalFetch);
            if (!validation.valid) {
              return Promise.reject(new Error("Validation failed"));
            }
            
            fire("cart:add", { oldCart: validation.oldCart, product: productData });
          } catch (err) {
            if (err.isMixTypeError) {
              return Promise.reject(err);
            }
            console.warn("Validation error:", err);
          }
        }
        
        // Execute with strict rate limiting
        return await CartRequestManager.execute(requestKey, async () => {
          const response = await originalFetch.apply(this, args);

          // Fire cart changed event after successful add
          if (response.ok) {
            fire("cart:changed", { cart: await response.clone().json() });

            originalFetch("/cart/update.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ attributes: { cart_session: "" } }),
            }).catch((error) => console.warn("Failed to clear cart_session:", error));
          }

          return response;
        });
      }

      // Handle /cart/change and /cart/update with rate limiting
      if (urlString.includes("/cart/change") || urlString.includes("/cart/update")) {
        const requestKey = `cart-${urlString.includes("/cart/change") ? "change" : "update"}`;
        
        // Block if request is already in progress
        if (CartRequestManager.isInProgress(requestKey)) {
          console.log('🛑 Cart request blocked: already in progress');
          const error = new Error('Cart request already in progress');
          error.isDuplicateRequest = true;
          return Promise.reject(error);
        }
        
        return await CartRequestManager.execute(requestKey, async () => {
          const response = await originalFetch.apply(this, args);
          
          if (response.ok) {
            fire("cart:changed", { cart: await response.clone().json() });
          }
          
          return response;
        });
      }
      
      return originalFetch.apply(this, args);
    };

    // XHR handler for legacy code
    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      if (url.includes("/cart/add") || url.includes("/cart/change")) {
        this.addEventListener("load", async () => {
          if (this.status === 200) {
            try {
              const cart = JSON.parse(this.responseText);
              fire("cart:changed", { cart });
            } catch (e) {
              console.warn("Failed to parse cart response:", e);
            }
          }
        });
      }
      return originalXHROpen.call(this, method, url, ...rest);
    };
  });
})();

// ============================================================================
// EVENT LISTENERS (Optional - for debugging)
// ============================================================================
window.addEventListener("cart:changed", (e) => {
  console.log("Cart updated:", e.detail.cart);
});
